import { useContext, useEffect, useState } from 'react';
import { Box, Button, Stack, SvgIcon, useTheme, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Plus } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import { useNavigate, useLocation } from 'react-router-dom';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import BackButton from '../../../components/shared/actions/BackButton';
import CataloguesTab from './components/CataloguesTab';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

const CataloguesPage = () => {
  const { t } = useTranslation('catalogues');
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const { hasPermissionTo } = useContext(UserContext);
  const { defaultInstanceId, defaultMarketId, defaultFiltersLoading } = useInstanceMarket();
  const [hasPermissionToCreateCatalogue, setHasPermissionToCreateCatalogue] = useState<boolean>(false);

  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterMarket, setCurrentFilterMarket] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);

  useEffect(() => {
    setHasPermissionToCreateCatalogue(hasPermissionTo(['CatalogueCreate', 'ConfigurationAdmin']));

    const searchParams = new URLSearchParams(location.search);
    const companyIdParam = searchParams.get('companyId');
    const customerTypeIdParam = searchParams.get('customerTypeId');
    const searchTermParam = searchParams.get('searchTerm');

    setCurrentFilterInstance(companyIdParam || '');
    setCurrentFilterMarket(customerTypeIdParam || '');
    setCurrentFilterSearchTerm(searchTermParam || '');
  }, [hasPermissionTo, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !searchParams.get('customerTypeId') && !defaultFiltersLoading && defaultInstanceId && defaultMarketId) {
      setCurrentFilterInstance(defaultInstanceId);
      setCurrentFilterMarket(defaultMarketId);
      const params = new URLSearchParams();
      params.set('companyId', defaultInstanceId);
      params.set('customerTypeId', defaultMarketId);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [defaultFiltersLoading, defaultInstanceId, defaultMarketId]);

  const handleCreateNew = () => {
    navigate('/procurement/catalogues/create');
  };

  const handleFilterChange = (instance: string, market: string, searchTerm?: string) => {
    setCurrentFilterInstance(instance);
    setCurrentFilterMarket(market);
    const newSearchTerm = searchTerm !== undefined ? searchTerm : currentFilterSearchTerm;
    setCurrentFilterSearchTerm(newSearchTerm);

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (market) params.set('customerTypeId', market);
    if (newSearchTerm) params.set('searchTerm', newSearchTerm);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const canViewCatalogues = hasPermissionTo(['CatalogueView', 'ConfigurationAdmin']);

  if (!canViewCatalogues) {
    return null;
  }

  return (
    <PageArea>
      <BackButton
        handleClick={() => navigate('/settings')}
        title={t('management.backToSettings', 'Back to Settings')}
        isLink={false}
        styles={{ marginBottom: '20px' }} />
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 4, // Add margin bottom for spacing
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle>{t('management.pageTitle')}</PageTitle>
        </Box>

        {hasPermissionToCreateCatalogue && (
          <Box
            width={isMobile ? '100%' : 'auto'}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Button
              variant="primary"
              size="lg"
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              onClick={handleCreateNew}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('management.createNew')}
            </Button>
          </Box>
        )}
      </Stack>

      <CataloguesTab 
        onCreateNew={handleCreateNew}
        onFilterChange={handleFilterChange}
        initialInstance={currentFilterInstance}
        initialMarket={currentFilterMarket}
        initialSearchTerm={currentFilterSearchTerm} />
    </PageArea>
  );
};

export default CataloguesPage;
